import { Box, Typography, Card, TextField, InputAdornment } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SearchIcon from '@mui/icons-material/Search';
import { colors } from '@src/styles';
import { cardTitleSX, iconTileSX, monoFontFamily, profileCardSX, tonePillSX } from '../profileStyles';
import { useState, useMemo } from 'react';

export type OrganizationData = {
  id: string;
  fantasyName: string;
};

export type ProfileOrganizationsCardProps = {
  organizations: OrganizationData[];
  organizationsDisplayLimit: number;
  onLoadMoreOrganizations: () => void;
};

export const ProfileOrganizationsCard = ({
  organizations,
  organizationsDisplayLimit,
  onLoadMoreOrganizations,
}: ProfileOrganizationsCardProps) => {
  const [orgSearchTerm, setOrgSearchTerm] = useState('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrgSearchTerm(e.target.value);
  };

  const filteredOrganizations = useMemo(() => {
    if (!organizations) return [];

    const filtered = organizations.filter((org) => {
      const searchLower = orgSearchTerm.toLowerCase();
      return org.fantasyName.toLowerCase().includes(searchLower) || org.id.toLowerCase().includes(searchLower);
    });

    return filtered.slice(0, organizationsDisplayLimit);
  }, [organizations, orgSearchTerm, organizationsDisplayLimit]);

  const hasMoreOrganizations = useMemo(() => {
    if (!organizations) return false;

    const totalFiltered = organizations.filter((org) => {
      const searchLower = orgSearchTerm.toLowerCase();
      return org.fantasyName.toLowerCase().includes(searchLower) || org.id.toLowerCase().includes(searchLower);
    }).length;

    return totalFiltered > organizationsDisplayLimit;
  }, [organizations, orgSearchTerm, organizationsDisplayLimit]);

  const handleOrganizationsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrolledToBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 50;

    if (scrolledToBottom && hasMoreOrganizations) {
      onLoadMoreOrganizations();
    }
  };

  if (!organizations || organizations.length === 0) {
    return null;
  }

  return (
    <Card
      elevation={0}
      sx={{
        ...profileCardSX,
        height: { xs: 420, md: 340 },
        display: 'flex',
        flexDirection: 'column',
        padding: 2.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 2 }}>
        <Box sx={iconTileSX}>
          <ApartmentIcon />
        </Box>
        <Typography component="h2" sx={{ ...cardTitleSX, flex: 1 }}>
          Organizações
        </Typography>
        <Box sx={tonePillSX(colors.brand.textSecondary)}>{organizations.length}</Box>
      </Box>

      <TextField
        size="small"
        placeholder="Pesquisar por nome ou ID..."
        value={orgSearchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: colors.brand.textMuted, fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          marginBottom: 1.25,
          '& .MuiOutlinedInput-root': {
            height: 38,
            fontSize: '13.5px',
            color: colors.primary.white,
            backgroundColor: colors.background.primary,
            borderRadius: '8px',
            '& fieldset': { borderColor: colors.brand.line },
            '&:hover fieldset': { borderColor: 'rgba(255, 160, 21, 0.5)' },
            '&.Mui-focused fieldset': { borderColor: colors.primary.yellow, borderWidth: '1px' },
          },
          '& input::placeholder': { color: colors.brand.textMuted, opacity: 1 },
        }}
      />

      <Box onScroll={handleOrganizationsScroll} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', marginX: -1 }}>
        {filteredOrganizations.length > 0 ? (
          <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {filteredOrganizations.map((org) => (
              <Box
                component="li"
                key={org.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  padding: '8px 10px',
                  borderRadius: '8px',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: '7px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: colors.brand.textStrong,
                    fontFamily: monoFontFamily,
                    fontSize: '11px',
                    fontWeight: 600,
                  }}
                >
                  {org.fantasyName
                    .replace(/[^\p{L}\p{N}]/gu, '')
                    .charAt(0)
                    .toUpperCase() || '#'}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap title={org.fantasyName} sx={{ fontSize: '13.5px', color: colors.primary.white }}>
                    {org.fantasyName}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ fontFamily: monoFontFamily, fontSize: '11px', color: colors.brand.textMuted, mt: 0.25 }}
                  >
                    {org.id}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2 }}>
            <Typography sx={{ fontSize: '13px', color: colors.brand.textSecondary, textAlign: 'center' }}>
              Nenhuma organização encontrada
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};
